"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import type { FinancialSnapshotPayload } from "@/lib/types/financial-snapshot";
import { MetricPillRow } from "./MetricPillRow";
import { OverviewTab } from "./tabs/OverviewTab";
import { ProfitabilityTab } from "./tabs/ProfitabilityTab";
import { ValuationTab } from "./tabs/ValuationTab";

type TabKey = "overview" | "profitability" | "valuation";

const TABS: Array<{ key: TabKey; label: string; proOnly: boolean }> = [
  { key: "overview", label: "Financial Overview", proOnly: false },
  { key: "profitability", label: "Profitability & Leverage", proOnly: true },
  { key: "valuation", label: "Valuation", proOnly: true },
];

interface Props {
  payload: FinancialSnapshotPayload;
  currency: string;
  userTier: "free" | "pro" | "premium";
}

function logAnalytics(event: string, props: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  try {
    (
      window as unknown as {
        plausible?: (n: string, o?: { props?: Record<string, unknown> }) => void;
      }
    ).plausible?.(event, { props });
  } catch {
    /* noop */
  }
}

export function FinancialSnapshot({ payload, currency, userTier }: Props) {
  const [tab, setTab] = useState<TabKey>("overview");

  useEffect(() => {
    logAnalytics("financial_snapshot_tab_viewed", { tab, userTier });
  }, [tab, userTier]);

  const { chartData, narrative } = payload;
  const isPro = userTier !== "free";

  return (
    <section className="mt-8">
      <header className="mb-4">
        <h3 className="text-xl font-serif text-foreground">Financial Snapshot</h3>
        <p className="text-xs text-muted-foreground mt-1">
          5-year fundamentals at a glance. All charts computed from latest filings.
        </p>
      </header>

      <MetricPillRow metrics={chartData.currentMetrics} currency={currency} />

      <div className="flex border-b border-surface-light/10 mb-5">
        {TABS.map((t) => {
          const locked = t.proOnly && !isPro;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors ${
                active
                  ? "text-foreground border-b-2 border-blue -mb-px"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
              {locked && <span className="ml-1.5 text-[10px]">🔒</span>}
            </button>
          );
        })}
      </div>

      <div className="relative">
        {tab === "overview" && <OverviewTab data={chartData} narrative={narrative} />}
        {tab === "profitability" && (
          <LockableTabContent locked={!isPro}>
            <ProfitabilityTab data={chartData} narrative={narrative} />
          </LockableTabContent>
        )}
        {tab === "valuation" && (
          <LockableTabContent locked={!isPro}>
            <ValuationTab data={chartData} narrative={narrative} />
          </LockableTabContent>
        )}
      </div>
    </section>
  );
}

function LockableTabContent({
  locked,
  children,
}: {
  locked: boolean;
  children: React.ReactNode;
}) {
  if (!locked) return <>{children}</>;
  return (
    <div className="relative">
      <div className="pointer-events-none select-none blur-md opacity-40">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-surface border border-surface-light/20 rounded-xl px-6 py-5 text-center shadow-lg max-w-sm">
          <div className="text-sm font-semibold mb-1">Unlock full Financial Snapshot</div>
          <div className="text-xs text-muted-foreground mb-4">
            Profitability, leverage, and valuation charts with AI insights.
          </div>
          <Link
            href="/pricing"
            className="inline-block bg-blue text-white text-xs font-semibold px-4 py-2 rounded-md hover:bg-blue/90"
          >
            Upgrade to Pro
          </Link>
        </div>
      </div>
    </div>
  );
}
